import type { FunctionComponent } from 'react'

import { DebtSummaryEmptyState } from '@monthio/icons'
import { CurrencyDisplay, MaterialIcons } from '@monthio/react-components'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import type { RevampedDebtRecordDto } from '../types/debtOverviewApi.types'
import type { DebtItem } from './debtItem'

import { Conditionally } from '../../../modulesTemp/shared/conditionally'
import { ConditionalWrapper } from '../../../modulesTemp/shared/conditionalWrapper'
import { DeletedOverlay } from '../../../modulesTemp/shared/deletedOverlay'
import { ItemWithOptionalLogo } from '../../../modulesTemp/shared/ItemWithOptionalLogo'
import { DebtRecordRevampedType } from '../types/debtOverviewApi.types'
import { AddButton } from './addButton'
import { Column } from './column'
import { debtFallbackImageSrc } from './debtFallbackImage'
import { canonicalDebtRecord, isDebtDeleted } from './debtItem'
import { useDebtTypeText } from './debtOverviewUtilities'
import { useFormattedAmount } from './useFormattedAmount'

const SummaryDeletedText = styled.div`
	position: absolute;
	right: 8px;
	top: 50%;
	translate: 0 -50%;
	z-index: 20;
	display: flex;
	align-items: center;
	gap: 12px;
`

const SummaryCardInstallmentText = styled.span`
	font-size: 18px;
	font-style: normal;
	font-weight: 600;
`
const calculatePrincipalAmount = (
	total: number,
	debtRecord: RevampedDebtRecordDto,
) => total + (debtRecord.principalAmount ?? 0)

const DebtSummaryCard: FunctionComponent<{
	item: DebtItem
	goToItem: (id: string) => void
}> = ({ item, goToItem }) => {
	const { t } = useTranslation()
	const { id, creditor, debtPayment, debtRecords } = item
	const monthlyInstallment = useFormattedAmount(
		debtPayment.paymentAmount || 0,
		debtPayment.currency,
	)
	const debtTypeText = useDebtTypeText()
	const isDeleted = isDebtDeleted(canonicalDebtRecord(item))

	const goToThisItem = useCallback(() => goToItem(id), [goToItem, id])
	const principalAmount = debtRecords.reduce(calculatePrincipalAmount, 0)
	const Logo =
		creditor?.logo ||
		debtFallbackImageSrc(
			canonicalDebtRecord(item).debtType || DebtRecordRevampedType.Unknown,
		)
	return (
		<ConditionalWrapper
			condition={isDeleted}
			wrap={(children) => (
				<DeletedOverlay onClick={goToThisItem} $isClickable>
					{children}
					<SummaryDeletedText>
						<SummaryCardInstallmentText>
							{t('debtOverview-summary-item-deleted')}
						</SummaryCardInstallmentText>
						<MaterialIcons.ArrowForwardIos />
					</SummaryDeletedText>
				</DeletedOverlay>
			)}
		>
			<ItemWithOptionalLogo
				title={debtTypeText(canonicalDebtRecord(item).debtType)}
				description={
					<Conditionally condition={!isDeleted}>
						<CurrencyDisplay
							amount={principalAmount}
							currency={debtPayment.currency}
						/>
					</Conditionally>
				}
				isRounded
				logoSrc={creditor?.logo}
				logo={<Logo viewBox="10 10 60 60" />}
				onClick={goToThisItem}
			>
				<Conditionally condition={!isDeleted}>
					<SummaryCardInstallmentText>
						{t('debtOverview-summary-item-amount-pr-month', {
							formattedAmount: monthlyInstallment,
						})}
					</SummaryCardInstallmentText>
					<MaterialIcons.ArrowForwardIos />
				</Conditionally>
			</ItemWithOptionalLogo>
		</ConditionalWrapper>
	)
}

const EmptyStateImg = styled(DebtSummaryEmptyState)`
	max-width: 800px;
`

export const DebtSummary: React.FC<{
	debtItems: DebtItem[]
	goToItem: (id: string) => void
	onAdd: () => void
}> = ({ debtItems, goToItem, onAdd }) => {
	return (
		<Column>
			{Array.from(debtItems) // don't change order on underlying list during sort
				.sort((x) =>
					!canonicalDebtRecord(x).status ||
					canonicalDebtRecord(x).status === 'active'
						? -1
						: 1,
				)
				.map((item) => (
					<DebtSummaryCard key={item.id} item={item} goToItem={goToItem} />
				))}
			{debtItems.length === 0 ? <EmptyStateImg /> : null}
			<AddButton onAdd={onAdd} />
		</Column>
	)
}
